import React, { useState, useEffect, useCallback } from 'react';
import { Steps, Button, InputNumber, Input } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import { appStateProxy } from '@/hooks/useAppState.ts';
import { settingsApi } from '@/API/settingsAPI.ts';
import onboardingAPI from '@/API/onboardingAPI.ts';
import type { Client } from '@spona/rest-api-types';
import './OnboardingWizard.css';

const { Step } = Steps;

const PLAN_LABEL: Record<string, string> = {
    starter: 'Starter',
    standard: 'Standard',
    pro: 'Pro',
    custom: 'Custom'
};

const OnboardingWizard: React.FC = () => {
    const appStateSnap = useSnapshot(appStateProxy);
    const [currentStep, setCurrentStep] = useState(0);
    const [isCompleting, setIsCompleting] = useState(false);

    // Step 1 — deadline state
    const [deadlineTo, setDeadlineTo] = useState<number>(7);
    const [companyInfoText, setCompanyInfoText] = useState<string>('');
    const [orderNoteText, setOrderNoteText] = useState<string>('');
    const [isSavingDeadline, setIsSavingDeadline] = useState(false);

    // Load current deadline when entering step 1
    useEffect(() => {
        if (currentStep === 1) {
            settingsApi.getDefaultDeadline().then(res => {
                if (res.success && res.data) {
                    setDeadlineTo(res.data.to ?? 7);
                }
            }).catch(() => {});
            settingsApi.getCompanyInfo().then(res => {
                if (res.success && res.data) {
                    setCompanyInfoText((res.data as any) as string ?? '');
                }
            }).catch(() => {});
            settingsApi.getOrderNote().then(res => {
                if (res.success && res.data) {
                    setOrderNoteText((res.data as any) as string ?? '');
                }
            }).catch(() => {});
        }
    }, [currentStep]);

    const client = appStateSnap.user?.client;
    const clientObj = typeof client === 'object' && client ? client as Client & { trialEndDate?: string } : null;

    const trialEndDate = clientObj?.trialEndDate
        ? new Date(clientObj.trialEndDate).toLocaleDateString('sr-RS', { day: '2-digit', month: 'long', year: 'numeric' })
        : null;
    const plan = clientObj?.plan ? PLAN_LABEL[clientObj.plan] ?? clientObj.plan : '—';
    const subscriptionStatus = clientObj?.subscriptionStatus;

    const markCompleted = useCallback(async () => {
        setIsCompleting(true);
        try {
            await onboardingAPI.completeOnboarding();
            if (appStateProxy.user) {
                appStateProxy.user.onboardingCompleted = true;
            }
        } catch {
            // Silently fail — the wizard closes either way
            if (appStateProxy.user) {
                appStateProxy.user.onboardingCompleted = true;
            }
        } finally {
            setIsCompleting(false);
        }
    }, []);

    const handleNext = useCallback(async () => {
        if (currentStep === 1) {
            // Save all settings before advancing
            setIsSavingDeadline(true);
            try {
                await Promise.all([
                    settingsApi.updateDefaultDeadline({ to: deadlineTo }),
                    settingsApi.updateCompanyInfo({ text: companyInfoText } as any),
                    settingsApi.updateOrderNote({ text: orderNoteText } as any),
                ]);
            } catch {
                // Non-blocking — proceed anyway
            } finally {
                setIsSavingDeadline(false);
            }
        }
        if (currentStep < 3) {
            setCurrentStep(s => s + 1);
        }
    }, [currentStep, deadlineTo, companyInfoText, orderNoteText]);

    const handleBack = useCallback(() => {
        setCurrentStep(s => s - 1);
    }, []);

    const handleFinish = useCallback(() => {
        markCompleted();
    }, [markCompleted]);

    const handleSkip = useCallback(() => {
        markCompleted();
    }, [markCompleted]);

    // ---- Render steps ----

    const renderWelcome = () => (
        <>
            <div className="spona-onboarding-step-title">Dobrodošli u Sponu!</div>
            <div className="spona-onboarding-step-desc">
                Vaš nalog je uspešno kreiran. Imate <strong>30 dana besplatnog probnog perioda</strong> da isprobate sve funkcionalnosti.
            </div>

            {subscriptionStatus === 'trial' && (
                <div className="spona-onboarding-trial-card">
                    <div className="spona-onboarding-trial-row">
                        <span className="spona-onboarding-trial-label">Plan</span>
                        <span className="spona-onboarding-trial-value">{plan}</span>
                    </div>
                    <div className="spona-onboarding-trial-row">
                        <span className="spona-onboarding-trial-label">Status</span>
                        <span className="spona-onboarding-trial-value">Probni period</span>
                    </div>
                    {trialEndDate && (
                        <div className="spona-onboarding-trial-row">
                            <span className="spona-onboarding-trial-label">Probni period ističe</span>
                            <span className="spona-onboarding-trial-value">{trialEndDate}</span>
                        </div>
                    )}
                </div>
            )}

            <ul className="spona-onboarding-feature-list">
                <li>
                    <span className="spona-onboarding-feature-icon">✓</span>
                    Upravljanje narudžbinama i kupcima
                </li>
                <li>
                    <span className="spona-onboarding-feature-icon">✓</span>
                    Katalog proizvoda i varijante proizvoda
                </li>
                <li>
                    <span className="spona-onboarding-feature-icon">✓</span>
                    Generisanje PDF predračuna i radnih naloga
                </li>
                <li>
                    <span className="spona-onboarding-feature-icon">✓</span>
                    Upravljanje timom i dozvolama
                </li>
            </ul>
        </>
    );

    const renderSettings = () => (
        <>
            <div className="spona-onboarding-step-title">Osnovna podešavanja</div>
            <div className="spona-onboarding-step-desc">
                Podesite podrazumevane vrednosti za narudžbine. Sve ovo možete promeniti u bilo kom trenutku iz Podešavanja.
            </div>

            <label className="spona-onboarding-field-label">
                Podrazumevani rok isporuke
            </label>
            <div className="spona-onboarding-deadline-row">
                <InputNumber
                    min={0}
                    max={365}
                    value={deadlineTo}
                    onChange={v => setDeadlineTo(v ?? 0)}
                    style={{ width: 90 }}
                    addonBefore="Do"
                />
                <span className="spona-onboarding-deadline-sep">dana</span>
            </div>

            <label className="spona-onboarding-field-label" style={{ marginTop: '1.25rem' }}>
                Informacije o firmi
                <span className="spona-onboarding-field-hint">Prikazuje se na predračunima i radnim nalozima</span>
            </label>
            <Input.TextArea
                rows={3}
                value={companyInfoText}
                onChange={e => setCompanyInfoText(e.target.value)}
                placeholder="npr. Naziv firme, adresa, PIB, telefon..."
                className="spona-onboarding-textarea"
            />

            <label className="spona-onboarding-field-label" style={{ marginTop: '1.25rem' }}>
                Podrazumevana napomena
                <span className="spona-onboarding-field-hint">Dodaje se na svaki novi nalog automatski</span>
            </label>
            <Input.TextArea
                rows={2}
                value={orderNoteText}
                onChange={e => setOrderNoteText(e.target.value)}
                placeholder="npr. Hvala na poverenju!"
                className="spona-onboarding-textarea"
            />
        </>
    );

    const renderTeam = () => (
        <>
            <div className="spona-onboarding-step-title">Pozovite tim</div>
            <div className="spona-onboarding-step-desc">
                Koristite Sponu sami ili sa celim timom.
            </div>

            <div className="spona-onboarding-info-box">
                <p style={{ margin: '0 0 0.75rem' }}>
                    Korisnikе možete dodati kroz <strong>Podešavanja → Korisnici</strong>.
                    Za svakog korisnika možete podesiti ulogu i tačne dozvole (admin, prodavac, radnik u produkciji...)
                </p>
                <p style={{ margin: 0 }}>
                    Grupe vam pomažu da organizujete tim i ograničite vidljivost narudžbina po sektorima ili prodajnim kanalima.
                </p>
            </div>
        </>
    );

    const renderDone = () => (
        <>
            <span className="spona-onboarding-done-icon">
                <CheckCircleOutlined style={{ color: 'var(--spona-brand-primary)' }} />
            </span>
            <div className="spona-onboarding-step-title" style={{ textAlign: 'center' }}>Sve je spremno!</div>
            <div className="spona-onboarding-step-desc" style={{ textAlign: 'center' }}>
                Vaš nalog je podešen i možete ga koristiti odmah. Kliknite <strong>Završi</strong> da počnete.
            </div>
        </>
    );

    const stepContent = [renderWelcome, renderSettings, renderTeam, renderDone];

    return (
        <div className="spona-onboarding-overlay">
            <div className="spona-onboarding-modal">
                {/* Header */}
                <div className="spona-onboarding-header">
                    <img
                        className="spona-onboarding-logo"
                        src="/logos/spona-default-logo-text.png"
                        alt="Spona"
                    />
                    <Steps
                        current={currentStep}
                        size="small"
                        className="spona-onboarding-steps"
                    >
                        <Step title="Dobrodošli" />
                        <Step title="Podešavanja" />
                        <Step title="Tim" />
                        <Step title="Gotovo" />
                    </Steps>
                </div>

                {/* Body */}
                <div className="spona-onboarding-body">
                    {stepContent[currentStep]()}
                </div>

                {/* Footer */}
                <div className="spona-onboarding-footer">
                    <button
                        className="spona-onboarding-skip"
                        onClick={handleSkip}
                        disabled={isCompleting}
                    >
                        Preskoči
                    </button>

                    <div className="spona-onboarding-footer-right">
                        {currentStep > 0 && (
                            <Button onClick={handleBack} disabled={isCompleting || isSavingDeadline}>
                                Nazad
                            </Button>
                        )}

                        {currentStep < 3 ? (
                            <Button
                                type="primary"
                                onClick={handleNext}
                                loading={isSavingDeadline}
                                disabled={isCompleting}
                            >
                                {currentStep === 0 ? 'Počni' : 'Dalje'}
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                onClick={handleFinish}
                                loading={isCompleting}
                                icon={<CheckCircleOutlined />}
                            >
                                Završi
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OnboardingWizard;
